// Builds a branded result image (PNG) from a section's metrics with no external
// dependencies. The card is drawn as an SVG string, then rasterized on a canvas.

export type ShareMetric = { label: string; value: string };

export type ShareCardOptions = {
  title: string;
  section?: string;
  metrics: ShareMetric[];
  site?: string;
};

const WIDTH = 1200;
const HEIGHT = 675;

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function buildCardSvg(opts: ShareCardOptions): string {
  const site = opts.site ?? "modernatc.com";
  const metrics = opts.metrics.slice(0, 8);
  const columns = metrics.length > 4 ? 3 : 2;
  const rows = Math.ceil(metrics.length / columns);
  const gridTop = 250;
  const gridLeft = 90;
  const gridRight = WIDTH - 90;
  const cellW = (gridRight - gridLeft) / columns;
  const cellH = Math.min(150, (HEIGHT - gridTop - 90) / Math.max(1, rows));

  const cells = metrics
    .map((metric, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const cx = gridLeft + col * cellW + cellW / 2;
      const cy = gridTop + row * cellH + cellH / 2;
      return `
        <text x="${cx.toFixed(0)}" y="${(cy - 14).toFixed(0)}" text-anchor="middle" font-family="Arial, sans-serif" font-size="52" font-weight="800" fill="#eef3f6">${escapeXml(metric.value)}</text>
        <text x="${cx.toFixed(0)}" y="${(cy + 30).toFixed(0)}" text-anchor="middle" font-family="Arial, sans-serif" font-size="22" fill="#aebcc6" letter-spacing="1">${escapeXml(metric.label.toUpperCase())}</text>`;
    })
    .join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${HEIGHT}" viewBox="0 0 ${WIDTH} ${HEIGHT}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#16242e"/>
      <stop offset="1" stop-color="#0a141b"/>
    </linearGradient>
  </defs>
  <rect width="${WIDTH}" height="${HEIGHT}" fill="url(#bg)"/>
  <rect x="0" y="0" width="${WIDTH}" height="8" fill="#3aa1bf"/>
  <circle cx="92" cy="86" r="20" fill="none" stroke="#3aa1bf" stroke-width="3"/>
  <circle cx="92" cy="86" r="5" fill="#3aa1bf"/>
  <text x="128" y="96" font-family="Arial, sans-serif" font-size="30" font-weight="900" letter-spacing="3" fill="#eef3f6">MODERN ATC</text>
  <text x="90" y="170" font-family="Arial, sans-serif" font-size="44" font-weight="800" fill="#eef3f6">${escapeXml(opts.title)}</text>
  ${opts.section ? `<text x="90" y="210" font-family="Arial, sans-serif" font-size="24" fill="#3aa1bf" letter-spacing="2">${escapeXml(opts.section.toUpperCase())}</text>` : ""}
  ${cells}
  <text x="90" y="${HEIGHT - 40}" font-family="Arial, sans-serif" font-size="22" fill="#aebcc6">${escapeXml(site)} · ATSA-format practice</text>
  <text x="${WIDTH - 90}" y="${HEIGHT - 40}" text-anchor="end" font-family="Arial, sans-serif" font-size="20" fill="#788794">Practice tool. Not affiliated with the FAA or Pearson VUE.</text>
</svg>`;
}

async function svgToPngBlob(svg: string): Promise<Blob> {
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not render the result image."));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas is unavailable in this browser.");
    context.drawImage(image, 0, 0, WIDTH, HEIGHT);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Image export failed."))), "image/png");
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadShareCard(opts: ShareCardOptions): Promise<void> {
  const blob = await svgToPngBlob(buildCardSvg(opts));
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const slug = (opts.section ?? "atsa").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  anchor.href = url;
  anchor.download = `modernatc-${slug}-result.png`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function copyShareCard(opts: ShareCardOptions): Promise<boolean> {
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) return false;
  try {
    const blob = await svgToPngBlob(buildCardSvg(opts));
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
    return true;
  } catch {
    return false;
  }
}
